import { useState, type SyntheticEvent } from "react";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import { createDemoCardPayment, type DemoPayoutSpeed } from "@/api/public";
import { pickForeground } from "@/lib/brand";
import { formatCurrency } from "@/lib/format";
import { cn } from "@/lib/utils";

/** On every made-up screen (spec D11), so a prospect never takes a demo for real money. */
export function DemoBadge({ className }: { className?: string }) {
  return (
    <span
      className={cn(
        "inline-flex items-center rounded-full border border-amber-500/30 bg-amber-500/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-amber-700 dark:text-amber-400",
        className,
      )}
    >
      Demo
    </span>
  );
}

const PAYOUTS: Array<{ value: DemoPayoutSpeed; label: string; detail: string }> = [
  { value: "Standard", label: "Standard payout", detail: "Lands in the bank in 2 working days" },
  { value: "Instant", label: "Instant payout", detail: "Lands today, less a 1% fee" },
];

/**
 * The pay page's card form for a demo organisation's invoice: no Stripe.js, a test card filled in, and the payout speed
 * the demo's fake Stripe pays it out at. Paying records a real card payment on the invoice.
 */
export function DemoCardForm({
  token,
  amount,
  currency,
  brand,
  onSucceeded,
}: {
  token: string;
  amount: number;
  currency: string;
  brand: string;
  onSucceeded: () => void;
}) {
  const [speed, setSpeed] = useState<DemoPayoutSpeed>("Standard");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: SyntheticEvent) {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    setError(null);
    const result = await createDemoCardPayment(token, amount, speed);
    if (result.ok) {
      onSucceeded();
      return;
    }
    setError(
      result.error.kind === "network"
        ? "Couldn't reach the payment service. Check your connection and try again."
        : "The demo payment didn't go through. Please try again.",
    );
    setSubmitting(false);
  }

  const field = "flex h-10 items-center rounded-md border bg-muted/40 px-3 text-sm tabular-nums text-muted-foreground";

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="rounded-lg border border-dashed border-amber-500/40 p-4">
        <div className="mb-3 flex items-center justify-between">
          <span className="text-sm font-medium">Card details</span>
          <DemoBadge />
        </div>
        <div className={field} aria-label="Card number">
          4242 4242 4242 4242
        </div>
        <div className="mt-2 grid grid-cols-2 gap-2">
          <div className={field} aria-label="Expiry">
            12 / 29
          </div>
          <div className={field} aria-label="Security code">
            123
          </div>
        </div>
        <p className="mt-3 text-xs text-muted-foreground">A demo test card. No money moves and nothing reaches a card network.</p>
      </div>

      <fieldset className="space-y-2">
        <legend className="mb-2 text-sm font-medium">When the business is paid</legend>
        {PAYOUTS.map((p) => (
          <label
            key={p.value}
            className={cn(
              "flex cursor-pointer items-start gap-3 rounded-lg border p-3 transition-colors",
              speed === p.value ? "border-foreground/40 bg-muted/50" : "hover:bg-muted/30",
            )}
          >
            <input
              type="radio"
              name="payout-speed"
              value={p.value}
              checked={speed === p.value}
              onChange={() => setSpeed(p.value)}
              className="mt-1 accent-current"
            />
            <span className="grid gap-0.5">
              <span className="text-sm font-medium">{p.label}</span>
              <span className="text-xs text-muted-foreground">{p.detail}</span>
            </span>
          </label>
        ))}
      </fieldset>

      {error ? <p className="text-sm text-destructive">{error}</p> : null}
      <Button
        type="submit"
        disabled={submitting}
        className="w-full"
        style={{ backgroundColor: brand, color: pickForeground(brand) }}
      >
        {submitting ? <Spinner className="h-4 w-4" /> : `Pay ${formatCurrency(amount, currency)}`}
      </Button>
    </form>
  );
}
